import type React from 'react'
import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ITextBlockProps } from '../../TextBlock'

import { ImagePoster } from 'atoms/ImagePoster'
import { LabelsOrder } from 'atoms/Label/Label.h'
import {
  LabelAgeRating,
  LabelContentBadge,
  LabelPurchaseBadge,
  LabelVerticalVideo,
} from 'atoms/Label/Templates'
import {
  TextBoxMaxLine2Font4xl,
  TextBoxMaxLine4FontSm,
} from 'atoms/TextBox/Templates'
import { Colors } from 'atoms/TextBox/TextBox.h'
import { LabelGroupTemplate3MaxLabels6 } from 'blocks/LabelGroup/Templates'
import { TextBlock } from '../../TextBlock'
import { TextBlockRowGap } from '../../TextBlock.h'
import {
  BannerLogoSC,
  BannerTopLabelsSC,
} from './TextBlockBannerHeadlineExtra10.styled'

export interface ITextBlockBannerHeadlineExtra10Props extends ITextBlockProps {
  children?:
    | React.ReactElement<ITextBoxProps>
    | React.ReactElement<ITextBoxProps>[]
  title: ITextBoxProps['title']
  description?: ITextBoxProps['title']
  /** Channel logo shown above the title on channel banners */
  logoUrl?: string
  labels?: string[]
  /** WBS content labels (New Movie, etc.) rendered first in the row above the title. */
  contentLabels?: string[]
  quality?: string
  dolbyAtmos?: boolean
  verticalVideo?: boolean
  lockedColor?: ITextBoxProps['color']
  locked?: boolean
  /** Content that has to be bought — the crown badge above the title. */
  paid?: boolean
  /** Rented content — remaining availability, such as `30 days`. */
  rented?: boolean
  purchaseTitle?: string
  /** PIN-locked VOD/TV Show banners keep this badge above the title. */
  ageRatingLabel?: string
}

export const TextBlockBannerHeadlineExtra10 = ({
  title,
  description,
  logoUrl,
  width = 100,
  labels,
  contentLabels,
  quality,
  dolbyAtmos,
  verticalVideo,
  rowGap = TextBlockRowGap.INDENT_20,
  lockedColor = Colors.TEXT_ICON_SECONDARY,
  locked,
  paid,
  rented,
  purchaseTitle,
  ageRatingLabel,
  ...rest
}: ITextBlockBannerHeadlineExtra10Props): JSX.Element => {
  const purchaseBadge = Boolean(paid) || Boolean(rented && purchaseTitle)
  const lockedAgeRating = Boolean(locked && ageRatingLabel)
  const showTopLabels =
    lockedAgeRating ||
    (!locked &&
      (verticalVideo || purchaseBadge || Boolean(contentLabels?.length)))

  return (
    <TextBlock width={width} rowGap={rowGap} {...rest}>
      {logoUrl && !locked && (
        <BannerLogoSC>
          <ImagePoster posterUrl={logoUrl} alt='' lazy={false} />
        </BannerLogoSC>
      )}
      {showTopLabels && (
        <BannerTopLabelsSC>
          {!locked &&
            contentLabels?.map((contentLabel) => (
              <LabelContentBadge key={contentLabel} title={contentLabel} />
            ))}
          {!locked && purchaseBadge && (
            <LabelPurchaseBadge title={rented ? purchaseTitle : ''} />
          )}
          {!locked && verticalVideo && (
            <LabelVerticalVideo
              data-testid='test-vertical-video-label'
              // Override the atom default (ORDER_3) so visual LTR follows DOM order.
              order={LabelsOrder.ORDER_1}
            />
          )}
          {lockedAgeRating && (
            <LabelAgeRating
              data-testid='age-rating-label'
              title={ageRatingLabel ?? ''}
            />
          )}
        </BannerTopLabelsSC>
      )}
      <TextBoxMaxLine2Font4xl title={title} />
      {(Boolean(labels?.length) || quality || dolbyAtmos) && !locked && (
        <LabelGroupTemplate3MaxLabels6
          list={labels ?? []}
          quality={quality}
          dolbyAtmos={dolbyAtmos}
        />
      )}
      {description && (
        <TextBoxMaxLine4FontSm
          color={locked ? lockedColor : Colors.TEXT_ICON_PRIMARY}
          preWrap
        >
          {description}
        </TextBoxMaxLine4FontSm>
      )}
    </TextBlock>
  )
}
